import {
  AlertTriangle,
  Save,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useUIStore } from "@/store/uiStore";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export function NavigationConfirmModal() {
  const { t } = useTranslation();
  const {
    modals,
    navigationLock,
    cancelNavigation
  } = useUIStore();
  const navigate = useNavigate();

  const isOpen = modals.navigationConfirm;
  const lockReason = navigationLock.lockReason || t('navigationConfirmModal.defaultLockReason');

  const handleConfirm = () => {
    const pendingNav = navigationLock.pendingNavigation;
    
    // Unlock navigation first
    useUIStore.setState((state) => ({
      navigationLock: {
        isLocked: false,
        lockReason: undefined,
        pendingNavigation: undefined
      },
      modals: {
        ...state.modals,
        navigationConfirm: false
      }
    }));
    
    // Navigations intercepted by the router blocker are resumed by
    // NavigationBlocker via blocker.proceed() once the lock is released.
    if (pendingNav?.type === 'blocker') {
      return;
    }

    // Handle programmatic navigation queued by useNavigateWithLock
    if (pendingNav?.path !== undefined) {
      if (typeof pendingNav.path === 'string') {
        navigate(pendingNav.path, {
          replace: pendingNav.options?.replace,
          state: pendingNav.options?.state
        });
      } else if (typeof pendingNav.path === 'number') {
        navigate(pendingNav.path);
      }
    }
  };

  const handleCancel = () => {
    cancelNavigation();
  };

  // Closing via Escape or the overlay means "stay here": clear the pending
  // navigation so the router blocker gets reset too.
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      cancelNavigation();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <DialogTitle>{t('navigationConfirmModal.title')}</DialogTitle>
          </div>
          <DialogDescription>
            {t('navigationConfirmModal.description', { lockReason })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2 sm:justify-end">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            {t('navigationConfirmModal.stayHere')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            className="flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {t('navigationConfirmModal.leaveAnyway')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
